<template>
  <TransitionGroup name="pagination-effect-list">
    <div
      class="product-item"
      v-for="product in products"
      v-bind:data-product-id="product.id"
      v-bind:data-choice-id="getDefaultChoice(product)?.id"
      :key="productListKey(product)"
    >
      <!-- Toujours une carte radio dans la liste : l'offre selectionnee ne
           doit pas disparaitre (ancien swap vers ChoiceList). Le resume
           detaille reste dans OrderSummary. -->
      <div
        class="vg-orderpage-card"
        :class="{
          'vg-orderpage-card--selected': isSelected(product),
          'vg-orderpage-card--disabled': isChoiceDisabled(product),
          'vg-orderpage-card--radio-mode': isRadioMode,
        }"
        role="radio"
        :aria-checked="isSelected(product) ? 'true' : 'false'"
        :aria-disabled="isChoiceDisabled(product) ? 'true' : 'false'"
        tabindex="0"
        @click="onCardClick(product, $event)"
        @keydown.enter.prevent="onCardClick(product, $event)"
        @keydown.space.prevent="onCardClick(product, $event)"
      >
        <div
          class="product-item-info row align-items-center"
          :class="{
            'product-item-info-disabled': isChoiceDisabled(product),
          }"
        >
          <ProductListImage
            :product="product"
            is-from-list
            :with-placeholder="showImagePlaceholder"
            :isProductListFromParent="isProductListFromParent"
            :alt="offerImageAlt(product)"
          />
          <div
            class="product-item-details"
            :class="hasImage(product) ? 'col-sm-4' : 'col-auto'"
          >
            <h3 class="product-item-name">
              <!-- FORGE-412 : sur les cartes d'offres, on n'interprete
                     pas le HTML des champs Salesforce. Le label et la
                     description sont rendus en texte plat ; la mise en
                     valeur typographique est portee par le CSS de la
                     carte (poids du titre, etc.). La version HTML
                     d'origine reste rendue dans le mini-summary
                     (OrderSummary.vue) pour conserver les <strong> et
                     bullets marketing. -->
              <ChoiceVanguardLabel
                :choice="getDefaultChoice(product)"
                plain-text
              />
            </h3>
            <div class="small-text">
              <ChoiceVanguardDescription
                :choice="getDefaultChoice(product)"
                plain-text
              />
            </div>
            <div class="listing-long-name">
              {{ product.longName }}
            </div>
            <div class="free-label">
              <ChoiceFreeLabel :choice="getDefaultChoice(product)" />
            </div>
          </div>
          <template v-if="getDefaultChoice(product)">
            <Price
              :product="getDefaultChoice(product)"
              :class="
                hasImage(product)
                  ? 'price-container col-sm-3'
                  : 'price-container col-auto ms-auto'
              "
            />
          </template>
          <template
            v-if="
              showaddtocart &&
              getDefaultChoice(product) &&
              !(isRadioMode && isSelected(product))
            "
          >
            <AddToCart
              :item="fillProductWithChoice(product)"
              :btnlabel="getAddToCartLabel()"
              :forceDisabled="isChoiceDisabled(product)"
              :disabledTooltip="getChoiceDisabledTooltip(product)"
              class="col-sm-3 add-to-cart-col"
              @click.stop
            />
          </template>
          <!-- Lien catalogue (fiche produit) : uniquement quand add-to-cart
                 est desactive. Ne pas confondre avec la carte radio selectionnee
                 (showaddtocart=true mais bouton masque). -->
          <a
            v-else-if="!showaddtocart && product.url"
            :href="product.url"
            class="col-sm-2"
            @click.stop
          >
            <span class="goto-product">{{ $t("labels.more") }}</span>
          </a>
        </div>
        <!--
            FORGE-412 — Quick-sell sur carte non-selectionnee.

            Historiquement, le `<CartTableUpsellList>` n'etait rendu que
            depuis `ChoiceList.vue` (carte selectionnee). L'utilisateur
            ne voyait donc l'offre quick-sell qu'apres avoir choisi
            l'offre parente, ce qui en cachait la valeur marchande au
            moment de la decision.

            On le rend ici aussi en mode radio, sous la zone produit, en
            passant un cartItem synthetique construit a partir du
            product + son choice par defaut. Le composant UpsellList
            consomme `cartItem.choice.id` pour matcher l'upsell dans
            `productDetails.upsells`, et le `toggleAction` appelle
            `CartService.addToCart(cartItem)` + `upselledStore.upgradeChoice`.

            Effet de bord voulu : un click sur le toggle d'une carte
            non-selectionnee ajoute l'upsell choice au cart, ce que
            `isSelected(product)` detecte via le mapping downgrade du
            store. La carte bascule donc automatiquement en mode
            selectionne (rendue via `ChoiceList.vue`), satisfaisant la
            consigne "si on selectionne via le toggle, ca selectionne
            automatiquement l'offre".

            On limite au mode radio : en mode cart (limitCartTo1Item
            false), la promotion d'un upsell directement depuis une
            carte non-selectionnee melerait deux interactions distinctes
            (ajout au panier + upgrade) sur un meme bouton, ce que la
            page panier multi-offres n'attend pas.
          -->
        <CartTableUpsellList
          v-if="
            isRadioMode &&
            shouldShowUpsellForProduct(product) &&
            upsellCartItemForCard(product)
          "
          :cartItem="upsellCartItemForCard(product)"
          :parent-choice-id="getDefaultChoice(product)?.id"
        />
      </div>
    </div>
  </TransitionGroup>
</template>

<script>
import AddToCart from "@/components/AddToCart.vue";
import Price from "@/components/productlist/subcomponents/Price.vue";
import Utils from "@/services/utils";
import Ratings from "@/components/product/Ratings.vue";
import ProductListImage from "@/components/productlist/subcomponents/Image.vue";
import ChoiceVanguardLabel from "@/components/choice/VanguardLabel.vue";
import ChoiceFreeLabel from "@/components/choice/FreeLabel.vue";
import ChoiceVanguardDescription from "@/components/choice/VanguardDescription.vue";
import AlreadyOrderedProducts from "@/services/alreadyOrderedProducts";
import { useCartStore } from "@/store/cart";
import CartTableUpsellList from "@/components/cart/table/UpsellList.vue";
import OrderPageOffers from "@/services/orderPageOffers";
import { useUpselledStore } from "@/store/upselled";
import { hasRequestedChoiceIds } from "@/services/orderPageAddChoicesParam";
import { stripDecodedHtml } from "@/services/text";

export default {
  name: "OrderPageList",
  props: {
    products: {
      type: Array,
      default: () => [],
    },
    categoryid: {
      type: String,
      default: "",
    },
    showaddtocart: {
      type: Boolean,
      default: true,
    },
    addToCartLabel: {
      type: String,
    },
    showprocessorder: {
      type: Boolean,
      default: false,
    },
    getDefaultChoice: {
      type: Function,
      required: true,
    },
    fillProductWithChoice: {
      type: Function,
      required: true,
    },
    isProductListFromParent: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    CartTableUpsellList,
    ChoiceVanguardDescription,
    ChoiceFreeLabel,
    ChoiceVanguardLabel,
    ProductListImage,
    Ratings,
    AddToCart,
    Price,
  },
  data() {
    return {
      productDetails: null,
      alreadyOrderedProductsSalesforceId: [],
      blockedChoiceIds: [],
      // Cache d'objets cartItem synthetiques utilises uniquement pour
      // alimenter le `<CartTableUpsellList>` sur les cartes non-
      // selectionnees (cf. plus bas dans le template). La cle est
      // `product.id::choice.id` (cf. `cartItemForUpsell`) pour eviter
      // les collisions quand magellan-bundle emet plusieurs vues d'un
      // meme product Salesforce.
      //
      // On garde une reference stable pendant la duree de vie du
      // UpsellList synthetique : le toggle mute en place
      // `cartItem.choice` / `qty` / `qtyIncr`, donc un objet re-cree a
      // chaque render perdrait ces mutations et provoquerait des warns
      // Vue de reassignation reactive.
      //
      // On reset ce cache a chaque update du cart (cf. watch
      // `cartLastUpdate`) : sinon, apres un toggle, le cache retient un
      // `choice` qui pointe vers l'upsell, et si la carte redevient
      // non-selectionnee plus tard (l'utilisateur clique sur une autre
      // offre), `UpsellList.findUpsellChoices` voit ce mauvais state et
      // affiche le toggle en ON avec un libelle "downgrade" au lieu de
      // l'OFF d'une nouvelle suggestion d'upsell.
      upsellCartItemCache: {},
    };
  },
  computed: {
    cartLastUpdate() {
      return useCartStore().lastUpdate;
    },
    // FORGE-412 — radio-mode : carte compacte (radio + texte + prix), pas
    // d'image et pas de bouton add-to-cart visible. Active quand l'utilisateur
    // doit choisir UNE offre parmi plusieurs (limitCartTo1Item=true).
    isRadioMode() {
      return !!(this.productDetails && this.productDetails.limitCartTo1Item);
    },
    // FORGE-412 — placeholder image visible UNIQUEMENT en preview (la
    // matrice `?scenario=` injecte `globalConfig.previewMode = true`). En
    // prod (admin Salesforce), si une offre n'a pas d'image configuree on
    // ne montre pas de slot vide : le layout se compacte naturellement
    // (cf. hasImage() qui retombe sur la presence reelle d'un visuel).
    showImagePlaceholder() {
      return window.globalConfig?.previewMode === true;
    },
  },
  watch: {
    // FORGE-412 — chaque modification du cart invalide les cartItems
    // synthetiques accumules dans `upsellCartItemCache`. Sans ce reset,
    // un toggle quick-sell ayant mute `cached.choice` vers l'upsell
    // resterait en place : a la prochaine fois que la carte
    // redeviendrait non-selectionnee, le UpsellList synthetique lirait
    // un choice deja "upselle" et le toggle serait affiche en ON avec
    // un libelle de downgrade au lieu de l'OFF d'une nouvelle
    // suggestion. On force ainsi un cache propre apres chaque mutation
    // du cart (ajout, suppression, vidage radio-mode).
    cartLastUpdate() {
      this.upsellCartItemCache = {};
    },
  },
  methods: {
    offerImageAlt(product) {
      const choice = this.getDefaultChoice(product);
      return stripDecodedHtml(choice?.vanguardLabel || choice?.name || "");
    },
    hasImage(product) {
      // En preview, le slot est toujours rendu (placeholder neutre) — on
      // force donc le layout `col-sm-4` + `col-sm-3` pour les details et le
      // prix. En prod, on retombe sur la logique reelle : slot seulement si
      // une image est resolue depuis initialChoices, featuredImage ou main.
      if (this.showImagePlaceholder) {
        return true;
      }
      const choiceImage = this.getInitialChoiceImage(product);
      if (choiceImage) return true;

      const featuredImage = product?.images?.find(
        (image) => image.type === "featuredImage",
      );
      return !this.isProductListFromParent && featuredImage
        ? !!featuredImage.src
        : !!product?.images?.find((image) => image.type === "main")?.src;
    },
    getInitialChoiceImage(product) {
      const productDetails = window.productDetails;
      if (!productDetails?.initialChoices || !product?.choice?.id) {
        return null;
      }

      for (const choiceGroup of productDetails.initialChoices) {
        for (const choice of choiceGroup) {
          if (choice.id === product.choice.id && choice.image) {
            return choice.image;
          }
        }
      }

      return null;
    },
    // Construit (et memoise) un cartItem synthetique utilise pour
    // rendre le `<CartTableUpsellList>` sous une carte non-selectionnee.
    // Le composant UpsellList attend un `cartItem` possedant au minimum
    // `choice.id` (pour matcher l'upsell dans
    // `productDetails.upsells`) et un product de base
    // (`name`, `productId`, ...) qui sera passe a `CartService.addToCart`
    // si l'utilisateur active le toggle.
    //
    // On reutilise la meme reference d'objet pour un couple
    // (product.id, choice.id) : `toggleAction` mute `cartItem.choice`,
    // `cartItem.qty` et `cartItem.qtyIncr` ; un objet temporaire serait
    // perdu entre deux rerenders et casserait la mecanique d'ajout au
    // cart.
    //
    // ATTENTION : magellan-bundle peut emettre plusieurs "products"
    // partageant le meme `product.id` (Salesforce) mais avec des
    // `defaultChoice` distincts (cas d'une order page qui presente
    // plusieurs vues du meme product Salesforce, ex. abonnement
    // mensuel vs annuel). On ne peut donc PAS memoiser uniquement par
    // `product.id` : la 1ere vue ecraserait le cache et toutes les
    // suivantes recupereraient un `cartItem.choice` du mauvais
    // product. La cle inclut donc `choice.id` pour distinguer les
    // vues.
    //
    // On retourne `null` quand le product n'a pas de choice par defaut
    // affichable : le `v-if` cote template evite alors le rendu vide.
    // Quick-sell uniquement sur les cartes dont le choice par defaut a une
    // vente flash : la sienne, sinon celle de la page s'il s'agit d'une
    // offre de la page (pas d'une offre unitaire qui reutilise le meme
    // choice id comme enfant upsell).
    shouldShowUpsellForProduct(product) {
      const choice = this.getDefaultChoice(product);
      if (!choice?.id) {
        return false;
      }
      return OrderPageOffers.hasQuickSellForChoice(
        choice.id,
        window.productDetails,
      );
    },
    cartItemForUpsell(product) {
      if (!product?.id) return null;
      const choice = this.getDefaultChoice(product);
      if (!choice) return null;
      const cacheKey = `${product.id}::${choice.id}`;
      if (!this.upsellCartItemCache[cacheKey]) {
        this.upsellCartItemCache[cacheKey] = {
          ...product,
          choice,
          qty: 0,
          qtyIncr: 1,
        };
      }
      return this.upsellCartItemCache[cacheKey];
    },
    // Upsell sur carte liste : item reel du panier si selectionnee,
    // sinon cartItem synthetique (toggle quick-sell sur carte non-selectionnee).
    upsellCartItemForCard(product) {
      if (this.isSelected(product)) {
        const cart = this.getCart();
        const cartItem = cart?.items?.find((item) =>
          this.isCartItemMatchingProduct(item, product),
        );
        if (cartItem) {
          return cartItem;
        }
      }
      return this.cartItemForUpsell(product);
    },
    syncAlreadyOrderedProducts() {
      this.alreadyOrderedProductsSalesforceId = (
        AlreadyOrderedProducts.getAlreadyOrderedProducts() || []
      ).map((id) => String(id));
    },
    onAlreadyOrderedUpdated(event) {
      const productsSalesforceId = event?.detail?.productsSalesforceId;
      const blockedChoiceIds = event?.detail?.blockedChoiceIds;
      this.alreadyOrderedProductsSalesforceId = productsSalesforceId?.length
        ? productsSalesforceId.map((id) => String(id))
        : (AlreadyOrderedProducts.getAlreadyOrderedProducts() || []).map((id) =>
            String(id),
          );
      this.blockedChoiceIds = blockedChoiceIds?.length
        ? blockedChoiceIds.map((id) => String(id))
        : [];
    },
    isChoiceDisabled(product) {
      const choice = this.getDefaultChoice(product);
      const choiceId = choice?.id ? String(choice.id) : null;

      if (this.blockedChoiceIds.length) {
        return !!choiceId && this.blockedChoiceIds.includes(choiceId);
      }

      const productSalesforceId = choice?.salesforceProductId
        ? String(choice.salesforceProductId)
        : null;

      if (!productSalesforceId) {
        return false;
      }

      return this.alreadyOrderedProductsSalesforceId.includes(
        productSalesforceId,
      );
    },
    getChoiceDisabledTooltip(product) {
      const choice = this.getDefaultChoice(product);
      const choiceId = choice?.id ? String(choice.id) : null;
      if (!choiceId || !this.blockedChoiceIds.includes(choiceId)) {
        return "";
      }

      return this.$t("messages.alreadyClickedMessage");
    },
    displayDuration(duration, startingPrice = false) {
      return Utils.displayDuration(duration, this.$i18n.locale, startingPrice);
    },
    // Cle stable pour le v-for : magellan peut emettre plusieurs cartes
    // avec le meme product.id (Salesforce) mais un choice par defaut
    // different. Sans choice.id dans la cle, Vue reutilise le mauvais
    // sous-arbre DOM et un clic sur la 3e carte peut declencher
    // l'AddToCart de la 4e.
    productListKey(product) {
      const choice = this.getDefaultChoice(product);
      const choiceId = choice?.id ?? product?.choice?.id ?? "unknown";
      return `${product.id}::${choiceId}`;
    },
    getCart() {
      return useCartStore().cart;
    },
    // Associe une ligne du panier a la carte produit correspondante.
    // Quand le panier contient un choice upselle (ex. vente flash 21307
    // depuis le parent 21913), seule la carte du parent doit etre
    // consideree comme selectionnee — pas la carte dont le choice par
    // defaut EST l'enfant upsell (ex. abonnement CPT010OP), sinon
    // showCart() retourne true pour deux produits et ChoiceList est
    // rendu deux fois (doublon visuel apres activation du toggle).
    isCartItemMatchingProduct(cartItem, product) {
      if (cartItem?.isQuickSellStack) {
        return false;
      }

      const productDefaultChoice = this.getDefaultChoice(product);
      if (!productDefaultChoice) {
        return false;
      }

      const productDefaultId = String(productDefaultChoice.id);
      const downgradeChoice = useUpselledStore().getDowngradeChoice(
        cartItem.choice,
      );

      if (downgradeChoice) {
        return String(downgradeChoice.id) === productDefaultId;
      }

      return String(cartItem.choice?.id) === productDefaultId;
    },
    isSelected(product) {
      // Reactive dependency: re-evaluate when cart changes
      // eslint-disable-next-line no-unused-expressions
      this.cartLastUpdate;

      const cart = this.getCart();
      if (!cart?.items?.length) {
        return false;
      }

      return cart.items.some((item) =>
        this.isCartItemMatchingProduct(item, product),
      );
    },
    getAddToCartLabel() {
      if (this.productDetails && this.productDetails.limitCartTo1Item) {
        return this.$t("labels.selectOffer");
      }

      return this.addToCartLabel;
    },
    onCardClick(product, event) {
      // En mode cart (limitCartTo1Item=false), la carte n'est PAS un
      // controle de selection : l'unique point d'interaction pour ajouter
      // une offre est le bouton "+ Ajouter" dedie en bas a droite. Un clic
      // sur la carte elle-meme (image, titre, prix, zone vide) ne doit
      // rien declencher — sinon l'utilisateur ajoute des offres au panier
      // par accident en lisant la fiche produit.
      // En mode radio (limitCartTo1Item=true), la carte EST le bouton :
      // pas de bouton dedie visible, le clic carte est l'interaction
      // primaire de selection (radio button pattern).
      if (!this.isRadioMode) {
        return;
      }
      // Les controles internes (bouton add-to-cart, lien plus de details)
      // utilisent @click.stop pour empecher la propagation a la carte. Donc
      // si on est ici, le clic vient bien de la carte elle-meme et pas d'un
      // sous-controle, et il faut declencher le bouton add-to-cart.
      if (this.isChoiceDisabled(product)) {
        return;
      }
      if (this.isSelected(product)) {
        // Deja selectionne : un nouveau clic ne deselectionne pas (le pattern
        // d'un radio est non-toggleable). Pour deselectionner, l'utilisateur
        // doit cliquer sur une autre offre, ce qui empty le cart si
        // limitCartTo1Item=true (cf. AddToCart.addToCartAction).
        return;
      }
      // Delegue au bouton AddToCart imbrique pour conserver toute sa logique
      // (limitCartTo1Item, isPostUpsell, draft, etc.).
      const card = event?.currentTarget;
      const btn = card?.querySelector(".add-to-cart-btn");
      if (btn && !btn.disabled) {
        btn.click();
      }
    },
    // En radio mode (limitCartTo1Item=true), on pre-selectionne une offre au
    // chargement. Priorite :
    //   1. le choice configure via ProductDetail.DefaultChoice / Magellan
    //      `defaultChoiceId` (ou `choices[].default`)
    //   2. sinon la premiere offre eligibile (comportement historique)
    //
    // Garde-fous :
    //   - radio mode uniquement (en multi-cart on n'auto-ajoute jamais)
    //   - pas de choix impose par l'URL : quand le visiteur arrive depuis un
    //     bouton de landing qui porte `addChoices`, il a deja elu son offre.
    //     La preselection catalogue ne doit pas la remplacer.
    //   - cart vide uniquement : si le cart contient deja un item (retour
    //     navigateur, draft restaure, post-upsell flow), on respecte la
    //     selection existante
    //   - on saute les offres `isChoiceDisabled` (already-ordered ou
    //     blocked) pour ne pas tomber sur une carte non-clickable
    //
    // On declenche le clic sur le bouton DOM .add-to-cart-btn (meme chemin
    // que `onCardClick`) plutot que d'appeler CartService directement : ca
    // garde toute la logique d'AddToCart (limitCartTo1Item, isPostUpsell,
    // draft, tracking) en un seul endroit.
    resolvePreferredPreselectChoiceId() {
      const details = this.productDetails || window.productDetails || {};
      if (details.defaultChoiceId) {
        return String(details.defaultChoiceId);
      }
      const flagged = (details.choices || []).find(
        (choice) => choice?.default === true || choice?.default === 1,
      );
      return flagged?.id != null ? String(flagged.id) : null;
    },
    findOfferToAutoSelect() {
      const eligible = (product) =>
        this.getDefaultChoice(product) && !this.isChoiceDisabled(product);

      const preferredId = this.resolvePreferredPreselectChoiceId();
      if (preferredId) {
        const preferred = this.products.find((product) => {
          if (!eligible(product)) return false;
          const choiceId = this.getDefaultChoice(product)?.id;
          return choiceId != null && String(choiceId) === preferredId;
        });
        if (preferred) return preferred;
      }

      return this.products.find(eligible) || null;
    },
    hasChoiceRequestedByUrl() {
      return hasRequestedChoiceIds(new URLSearchParams(window.location.search));
    },
    maybeAutoSelectFirstOffer() {
      if (!this.isRadioMode) return;
      if (this.hasChoiceRequestedByUrl()) return;
      const target = this.findOfferToAutoSelect();
      if (!target) return;
      this.$nextTick(() => {
        // On re-check le cart au moment du tick : un draft / post-upsell
        // restaure de maniere async peut avoir rempli le cart entre temps,
        // auquel cas on ne touche pas a la selection existante.
        const cart = this.getCart();
        if (cart?.items?.length) return;
        const targetChoiceId = this.getDefaultChoice(target)?.id;
        if (!targetChoiceId) return;
        const productEl = document.querySelector(
          `.product-item[data-product-id="${CSS.escape(String(target.id))}"][data-choice-id="${CSS.escape(String(targetChoiceId))}"]`,
        );
        const btn = productEl?.querySelector(".add-to-cart-btn");
        if (btn && !btn.disabled) {
          btn.click();
        }
      });
    },
  },
  mounted() {
    this.productDetails = window.productDetails;
    this.syncAlreadyOrderedProducts();
    window.addEventListener(
      "already-ordered-updated",
      this.onAlreadyOrderedUpdated,
    );
    this.maybeAutoSelectFirstOffer();
  },
  unmounted() {
    window.removeEventListener(
      "already-ordered-updated",
      this.onAlreadyOrderedUpdated,
    );
  },
};
</script>

<style scoped>
.product-item-info-disabled {
  opacity: 0.55;
}
</style>
